// frontend/src/lib/paidExposureEngine.js

/**
 * Paid Reports Exposure Engine (Assessment → Money Layer) — MVP hardened
 *
 * Goals:
 * - Deterministic, defensible exposure numbers
 * - Multi-regime output (EU/UK/US_FED/US_STATE)
 * - Must NEVER throw (returns a best-effort object)
 *
 * FX POLICY (MVP):
 * - Revenue inputs are in StartAudit reporting currency (cfg/summary currency_code)
 * - For percent-of-revenue ceilings, revenue is converted into the regime currency when FX is available
 * - If FX is missing, we fall back to treating input revenue as "native" with an explicit fx_status
 */

import { convertCurrencyUsingFx } from "./fxRates";

function n(x, fallback = 0) {
  const v = Number(x);
  return Number.isFinite(v) ? v : fallback;
}

function clamp(x, lo, hi) {
  const v = n(x, lo);
  return Math.max(lo, Math.min(hi, v));
}

function upper(s) {
  return String(s || "").trim().toUpperCase();
}

function uniq(arr) {
  const out = [];
  const seen = new Set();
  (Array.isArray(arr) ? arr : []).forEach((x) => {
    const k = upper(x);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(k);
  });
  return out;
}

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return "—";
  }
}

/**
 * Severity tier 1..5 -> multiplier (LOCKED contract).
 */
function severityMultiplier(tier) {
  const t = clamp(tier, 1, 5);
  return [0, 0.8, 0.95, 1.1, 1.28, 1.5][t] || 1.1;
}

/**
 * Compliance multiplier (LOCKED contract):
 * 1.0 + (1 - score/100) * 0.45
 */
function complianceMultiplier(scorePct) {
  const s = clamp(scorePct, 0, 100);
  return 1.0 + (1 - s / 100) * 0.45;
}

/**
 * Regime modeling factors (LOCKED contract).
 */
function regimeModelFactor(regime) {
  const r = upper(regime);
  if (r === "US_FED") return 1.12;
  if (r === "US_STATE") return 0.94;
  return 1.0;
}

/**
 * Inferred Federal Overlay (heuristic).
 */
function inferFederalOverlayFromConfig(cfg, summary) {
  const textFields = [];
  const candidates = [
    cfg?.industry,
    cfg?.sector,
    cfg?.niche,
    cfg?.businessType,
    cfg?.companyDescription,
    cfg?.description,
    cfg?.useCase,
    cfg?.use_case,
    cfg?.vertical,
    cfg?.naics,
    cfg?.sic,
    summary?.industry,
    summary?.sector,
  ];

  candidates.forEach((v) => {
    if (typeof v === "string" && v.trim()) textFields.push(v.trim());
    else if (Array.isArray(v))
      v.forEach((x) => (typeof x === "string" && x.trim() ? textFields.push(x.trim()) : null));
  });

  const haystack = textFields.join(" | ").toLowerCase();
  if (!haystack) return { inferred: false, reasons: [] };

  const rules = [
    {
      key: "HIPAA / healthcare",
      hits: ["hipaa", "hhs", "ocr", "phi", "healthcare", "medical", "clinic", "hospital", "patient", "claims"],
    },
    {
      key: "financial / securities",
      hits: ["sec", "finra", "broker", "investment", "securities", "advisor", "ria", "bank", "lending", "cfpb", "glba"],
    },
    {
      key: "telecom / communications",
      hits: ["fcc", "telecom", "carrier", "sms", "robocall", "autodial", "tcpa", "communications"],
    },
    {
      key: "consumer / advertising",
      hits: ["ftc", "advertising", "marketing", "consumer", "dark pattern", "unfair", "deceptive"],
    },
    { key: "education / minors", hits: ["ferpa", "student records", "school", "education"] },
    { key: "employment / workplace", hits: ["eeoc", "employment", "workplace", "hiring", "hr"] },
  ];

  const reasons = [];
  rules.forEach((r) => {
    const matched = r.hits.some((h) => haystack.includes(String(h).toLowerCase()));
    if (matched) reasons.push(r.key);
  });

  return { inferred: reasons.length > 0, reasons };
}

function inferSelectedRegimes(jurisdictions) {
  const j = uniq(jurisdictions);
  const mapped = new Set();
  j.forEach((x) => {
    if (x === "US") mapped.add("US_FED");
    else mapped.add(x);
  });
  return Array.from(mapped);
}

function currencyForRegime(regime, defaultCurrency) {
  const r = upper(regime);
  if (r === "UK") return { code: "GBP", symbol: "£" };
  if (r === "EU") return { code: "EUR", symbol: "€" };
  if (r === "US_FED" || r === "US_STATE") return { code: "USD", symbol: "$" };
  return {
    code: defaultCurrency?.code || "USD",
    symbol: defaultCurrency?.symbol || "$",
  };
}

/**
 * US State ceiling map (MVP deterministic approximation).
 */
const US_STATE_CEILINGS_USD = {
  TX: 5_000_000,
  CA: 2_500_000,
  IL: 1_000_000,
  CO: 2_000_000,
  NY: 1_500_000,
  UT: 750_000,
};

const US_STATE_DEFAULT_CEILING_USD = 50_000;

function usStateWeightForCeiling(ceilingUsd) {
  const c = Math.max(0, n(ceilingUsd, 0));
  if (c >= 2_500_000) return 1.0;
  if (c >= 750_000) return 0.7;
  return 0.25;
}

function normalizeStateKey(x) {
  const s = upper(x);
  if (!s) return "";
  const m = s.match(/^US[_-]([A-Z]{2})$/);
  if (m && m[1]) return m[1];
  if (/^[A-Z]{2}$/.test(s)) return s;
  return s;
}

function resolveUsStateCeilingUsd(stateCodeOrId) {
  const key = normalizeStateKey(stateCodeOrId);
  if (!key) return US_STATE_DEFAULT_CEILING_USD;
  return US_STATE_CEILINGS_USD[key] || US_STATE_DEFAULT_CEILING_USD;
}

function statutoryCeiling(regime, revenueNative) {
  const rev = Math.max(0, n(revenueNative, 0));
  const r = upper(regime);

  if (r === "UK") return Math.max(17_500_000, 0.04 * rev);
  if (r === "EU") return Math.max(20_000_000, 0.04 * rev);
  if (r === "US_FED") return Math.max(10_000_000, 0.02 * rev);

  return Math.max(5_000_000, 0.01 * rev);
}

function baseRiskAmount(revenueNative, peoplePerMonth) {
  const rev = Math.max(0, n(revenueNative, 0));
  const ppl = Math.max(0, n(peoplePerMonth, 0));
  return 0.006 * rev + 12 * ppl;
}

function convertRevenueForRegime({ revenueInput, inputCurrency, regimeCurrency, fx }) {
  const from = upper(inputCurrency || "USD");
  const to = upper(regimeCurrency || from);

  if (from === to) return { revenueNative: revenueInput, fx_status: "NOT_REQUIRED" };

  const converted = convertCurrencyUsingFx({
    amount: revenueInput,
    from,
    to,
    fx,
  });

  if (typeof converted === "number" && Number.isFinite(converted)) {
    return { revenueNative: converted, fx_status: "OK" };
  }

  return { revenueNative: revenueInput, fx_status: "FAILED_FALLBACK_INPUT_AS_NATIVE" };
}

function computeRegimeExposure({
  regime,
  revenueInput,
  inputCurrency,
  regimeCurrency,
  peoplePerMonth,
  complianceScorePct,
  severityTier,
  fx,
}) {
  const r = upper(regime);

  const { revenueNative, fx_status } = convertRevenueForRegime({
    revenueInput,
    inputCurrency,
    regimeCurrency,
    fx,
  });

  const ceiling = statutoryCeiling(r, revenueNative);
  const base = baseRiskAmount(revenueNative, peoplePerMonth);
  const sevM = severityMultiplier(severityTier);
  const compM = complianceMultiplier(complianceScorePct);
  const modelF = regimeModelFactor(r);

  const structuredWorst = Math.min(ceiling, base * sevM * compM * modelF);
  const net = structuredWorst * 0.88;
  const floor = structuredWorst * 0.65;

  return {
    regime: r,
    enabled: true,
    statutory_ceiling_native: Math.round(ceiling),
    structured_worst_case_native: Math.round(structuredWorst),
    net_native: Math.round(net),
    floor_native: Math.round(floor),
    model_factor: modelF,
    fx_status,
  };
}

function pickUsStateAggregationMode(cfg) {
  const raw =
    cfg?.usStateAggregationMode ||
    cfg?.us_state_aggregation_mode ||
    cfg?.us_state_aggregation ||
    cfg?.usStateAggregation ||
    cfg?.stateAggregationMode ||
    "";

  const m = upper(raw);
  if (m === "SUM") return "SUM";
  if (m === "WEIGHTED" || m === "WEIGHT") return "WEIGHTED";
  return "MAX";
}

function aggregateUsState(per_state, mode) {
  const m = upper(mode || "MAX");
  const rows = Array.isArray(per_state) ? per_state : [];

  if (!rows.length) {
    return { statutory_ceiling_native: 0, structured_worst_case_native: 0, net_native: 0, floor_native: 0 };
  }

  if (m === "SUM") {
    return {
      statutory_ceiling_native: Math.round(rows.reduce((a, r) => a + n(r.ceiling_native, 0), 0)),
      structured_worst_case_native: Math.round(rows.reduce((a, r) => a + n(r.worst_native, 0), 0)),
      net_native: Math.round(rows.reduce((a, r) => a + n(r.net_native, 0), 0)),
      floor_native: Math.round(rows.reduce((a, r) => a + n(r.floor_native, 0), 0)),
    };
  }

  if (m === "WEIGHTED") {
    return {
      statutory_ceiling_native: Math.round(rows.reduce((a, r) => a + n(r.ceiling_native, 0) * n(r.weight, 0), 0)),
      structured_worst_case_native: Math.round(rows.reduce((a, r) => a + n(r.worst_native, 0) * n(r.weight, 0), 0)),
      net_native: Math.round(rows.reduce((a, r) => a + n(r.net_native, 0) * n(r.weight, 0), 0)),
      floor_native: Math.round(rows.reduce((a, r) => a + n(r.floor_native, 0) * n(r.weight, 0), 0)),
    };
  }

  // MAX default
  const maxBy = (key) =>
    rows.reduce((best, r) => (n(r[key], -Infinity) > n(best?.[key], -Infinity) ? r : best), rows[0]);

  const maxCeil = maxBy("ceiling_native");
  const maxWorst = maxBy("worst_native");
  const maxNet = maxBy("net_native");
  const maxFloor = maxBy("floor_native");

  return {
    statutory_ceiling_native: Math.round(n(maxCeil?.ceiling_native, 0)),
    structured_worst_case_native: Math.round(n(maxWorst?.worst_native, 0)),
    net_native: Math.round(n(maxNet?.net_native, 0)),
    floor_native: Math.round(n(maxFloor?.floor_native, 0)),
  };
}

/**
 * Public API (MUST NOT THROW):
 * computePaidReportExposure({ configForEngine, canonicalPayload })
 */
export function computePaidReportExposure({ configForEngine, canonicalPayload }) {
  try {
    const cfg = configForEngine || canonicalPayload?.config || {};
    const assessment = canonicalPayload?.assessment || {};
    const summary = canonicalPayload?.summary || {};

    const selected = inferSelectedRegimes(
      summary?.jurisdictions || cfg?.__jurisdictions_normalized || cfg?.jurisdictions
    );

    const inputCurrency = upper(summary?.currency_code || cfg?.currency_code || "USD");
    const revenueInput = n(summary?.annual_revenue_estimate ?? cfg?.annualTurnover, 0);
    const people = n(summary?.people_impacted_per_month ?? cfg?.userImpact, 0);

    const compliance = n(assessment?.compliance_score_pct ?? summary?.compliance_score_pct, 0);
    const severityTier = n(assessment?.severity_tier ?? canonicalPayload?.inputs_echo?.severityTier, 3);

    const selectedStates = cfg?.selectedStates || cfg?.states_selected || cfg?.us_states || [];
    const usStateAggMode = pickUsStateAggregationMode(cfg);

    const defaultCurrency = {
      code: inputCurrency || "USD",
      symbol: summary?.currency_symbol || cfg?.currency_symbol || "$",
    };

    const fx = canonicalPayload?.fx || null;

    const inferred = inferFederalOverlayFromConfig(cfg, summary);

    const selectedSet = new Set(selected.map(upper));
    const usContext = selectedSet.has("US_STATE") || selectedSet.has("US_FED");

    if (inferred.inferred && usContext && !selectedSet.has("US_FED")) selectedSet.add("US_FED");

    const finalRegimes = Array.from(selectedSet);

    const per_regime = {};

    finalRegimes.forEach((regime) => {
      const r = upper(regime);

      if (r === "US_STATE") {
        const currency = currencyForRegime("US_STATE", defaultCurrency);

        // Convert revenue to USD once (if FX present)
        const { revenueNative: revenueUsd, fx_status } = convertRevenueForRegime({
          revenueInput,
          inputCurrency,
          regimeCurrency: "USD",
          fx,
        });

        const base = baseRiskAmount(revenueUsd, people);
        const sevM = severityMultiplier(severityTier);
        const compM = complianceMultiplier(compliance);
        const modelF = regimeModelFactor("US_STATE");

        const states = uniq((Array.isArray(selectedStates) ? selectedStates : []).map(normalizeStateKey)).filter(Boolean);
        const effectiveStates = states.length ? states : ["(BASELINE)"];

        const per_state = effectiveStates.map((stateKey) => {
          const ceiling =
            stateKey === "(BASELINE)" ? US_STATE_DEFAULT_CEILING_USD : resolveUsStateCeilingUsd(stateKey);

          const structuredWorst = Math.min(ceiling, base * sevM * compM * modelF);
          const net = structuredWorst * 0.88;
          const floor = structuredWorst * 0.65;

          return {
            state: stateKey,
            ceiling_native: Math.round(ceiling),
            worst_native: Math.round(structuredWorst),
            net_native: Math.round(net),
            floor_native: Math.round(floor),
            weight: usStateWeightForCeiling(ceiling),
          };
        });

        const agg = aggregateUsState(per_state, usStateAggMode);

        per_regime[r] = {
          regime: "US_STATE",
          enabled: true,
          statutory_ceiling_native: agg.statutory_ceiling_native,
          structured_worst_case_native: agg.structured_worst_case_native,
          net_native: agg.net_native,
          floor_native: agg.floor_native,
          currency_code: currency.code,
          currency_symbol: currency.symbol,
          display_name: "US (States)",
          model_factor: modelF,
          fx_status,
          inferred_overlay: false,
          inferred_reasons: [],
          us_state_detail: {
            aggregation_mode: usStateAggMode,
            selected_states: states,
            per_state,
          },
          fx_basis: fx
            ? {
                provider: fx?.provider || "—",
                rate_date: fx?.rate_date || "—",
                retrieved_at_utc: fx?.retrieved_at_utc || "—",
                input_currency_code: inputCurrency,
              }
            : null,
        };

        return;
      }

      const currency = currencyForRegime(r, defaultCurrency);

      const exp = computeRegimeExposure({
        regime: r,
        revenueInput,
        inputCurrency,
        regimeCurrency: currency.code,
        peoplePerMonth: people,
        complianceScorePct: compliance,
        severityTier,
        fx,
      });

      const isInferredFed =
        r === "US_FED" && !selected.map(upper).includes("US_FED") && inferred.inferred && usContext;

      per_regime[r] = {
        ...exp,
        currency_code: currency.code,
        currency_symbol: currency.symbol,
        display_name:
          r === "US_FED" ? (isInferredFed ? "Inferred Federal Overlay" : "US (Federal)") : r,
        inferred_overlay: Boolean(isInferredFed),
        inferred_reasons: isInferredFed ? inferred.reasons : [],
        fx_basis: fx
          ? {
              provider: fx?.provider || "—",
              rate_date: fx?.rate_date || "—",
              retrieved_at_utc: fx?.retrieved_at_utc || "—",
              input_currency_code: inputCurrency,
            }
          : null,
      };
    });

    const combined_by_currency = {};
    Object.keys(per_regime).forEach((k) => {
      const e = per_regime[k];
      const code = e.currency_code || "USD";

      const bucket = combined_by_currency[code] || {
        currency_code: code,
        currency_symbol: e.currency_symbol || "$",
        combined_structured_worst_case_native: 0,
        combined_net_native: 0,
        combined_floor_native: 0,
        combined_statutory_ceiling_visible_native: 0,
        regimes: [],
      };

      bucket.regimes.push(k);
      bucket.combined_structured_worst_case_native += n(e.structured_worst_case_native, 0);
      bucket.combined_net_native += n(e.net_native, 0);
      bucket.combined_floor_native += n(e.floor_native, 0);
      bucket.combined_statutory_ceiling_visible_native += n(e.statutory_ceiling_native, 0);

      combined_by_currency[code] = bucket;
    });

    const headlineCode = combined_by_currency[inputCurrency]
      ? inputCurrency
      : Object.keys(combined_by_currency)[0] || inputCurrency;

    const headlineBucket = combined_by_currency[headlineCode] || {
      currency_code: inputCurrency,
      currency_symbol: defaultCurrency.symbol,
      combined_structured_worst_case_native: 0,
      combined_net_native: 0,
      combined_floor_native: 0,
      combined_statutory_ceiling_visible_native: 0,
      regimes: [],
    };

    return {
      version: "exposure.3.1",
      generated_at_iso: nowIso(),
      per_regime,
      combined_by_currency,
      headline: {
        currency_code: headlineBucket.currency_code,
        currency_symbol: headlineBucket.currency_symbol,
        combined_structured_worst_case_native: Math.round(headlineBucket.combined_structured_worst_case_native),
        combined_net_native: Math.round(headlineBucket.combined_net_native),
        combined_floor_native: Math.round(headlineBucket.combined_floor_native),
        combined_statutory_ceiling_visible_native: Math.round(
          headlineBucket.combined_statutory_ceiling_visible_native
        ),
        regimes: headlineBucket.regimes,
      },
      overlays: {
        inferred_federal_overlay: {
          inferred: Boolean(inferred.inferred && usContext),
          reasons: inferred.reasons,
        },
      },
      fx_summary: fx
        ? {
            provider: fx?.provider || "—",
            rate_date: fx?.rate_date || "—",
            retrieved_at_utc: fx?.retrieved_at_utc || "—",
            base_currency: fx?.base || fx?.base_currency || "EUR",
            input_currency_code: inputCurrency,
          }
        : null,
      us_state_aggregation: {
        mode: usStateAggMode,
        selected_states_count: Array.isArray(selectedStates) ? selectedStates.length : 0,
      },
    };
  } catch (err) {
    // Non-throwing contract: return a minimal but explicit error object
    return {
      version: "exposure.error",
      generated_at_iso: nowIso(),
      per_regime: {},
      combined_by_currency: {},
      headline: {
        currency_code: "USD",
        currency_symbol: "$",
        combined_structured_worst_case_native: 0,
        combined_net_native: 0,
        combined_floor_native: 0,
        combined_statutory_ceiling_visible_native: 0,
        regimes: [],
      },
      overlays: {},
      fx_summary: null,
      us_state_aggregation: { mode: "MAX", selected_states_count: 0 },
      __error: String(err?.message || err || "Exposure engine error"),
    };
  }
}